import { useAtomValue } from 'jotai'
import { motion, MotionConfig, type Transition } from 'framer-motion'
import { MessageSquareText } from 'lucide-react'
import { getTurnStartTime, useIsTurnLive } from '@renderer/chat/store/turn-status'
import { StreamingChatIndicator } from '@renderer/components/streaming-chat-indicator'
import { stagedActionEditStatesAtom } from '../../../atoms'
import { useViewNavigation } from '../../../view-atoms'
import { viewContextOf } from '../../../view-definitions'
import { useOpenActionChatDrawer } from '../../../views/action-view/use-action-chat-drawer'
import { useOpenMeetingChatDrawer } from '../../../views/meeting-view/use-meeting-chat-drawer'
import { MEETING_VIEW_ID } from '../../meeting/meeting-view-id'
import { ElapsedTime } from './active-chat-cell'

const entranceTransition: Transition = { duration: 0.15, ease: 'easeOut' }

/**
 * "Resume chat" row in the inset composer's header (HIG-5977): shown on a view
 * whose chat drawer is dismissed but whose thread exists — the same slot the
 * pending-chat rows occupy on Home, so the dismissed thread reads the same way
 * as any other conversation still in flight. Clicking slides the drawer back
 * up; while the turn is streaming the row carries the pending-chat dots and
 * elapsed time.
 */
export function ResumeChatBar() {
  const { topView } = useViewNavigation()
  const actionContext = viewContextOf(topView, 'action')
  const meetingContext = viewContextOf(topView, MEETING_VIEW_ID)
  const editStates = useAtomValue(stagedActionEditStatesAtom)
  const openActionDrawer = useOpenActionChatDrawer()
  const openMeetingDrawer = useOpenMeetingChatDrawer()

  // The action row is keyed on the edit state alone — deliberately narrower
  // than the drawer's mount gate (`ActionViewModel.chatId`). The row offers to
  // *resume* a conversation, so it appears only once an edit has been sent this
  // session, not for every action that happens to own a thread from before. A
  // meeting's entry only holds a thread opened on it, or the one its note
  // names (see `useMeetingChatThread`), so its own id is enough.
  const resume = actionContext?.actionId
    ? {
        chatId: editStates[actionContext.actionId]?.conversationId ?? undefined,
        open: Boolean(actionContext.chat),
        label: 'Resume chatting with this action',
        onOpen: openActionDrawer,
      }
    : meetingContext
      ? {
          chatId: meetingContext.chatId,
          open: Boolean(meetingContext.chat),
          label: 'Resume chatting with this meeting',
          onOpen: openMeetingDrawer,
        }
      : undefined

  const chatId = resume?.chatId
  const isStreaming = useIsTurnLive(chatId)

  if (!resume || !chatId || resume.open) {
    return null
  }

  // Same register as ActiveChats: a flat row inside the composer card, with
  // the card's own border and shadow as the container chrome. The quick
  // fade-up covers its appearance the instant the drawer starts closing.
  return (
    <MotionConfig reducedMotion="user">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={entranceTransition}
        className="flex flex-col border-b p-1"
      >
        <button
          onClick={resume.onOpen}
          className="hover:bg-element-hover text-foreground corner-smooth type-ui-sm flex w-full cursor-pointer items-center gap-2.5 rounded-xl bg-transparent px-1.5 py-1.5 text-left transition-colors"
        >
          {isStreaming ? (
            <StreamingChatIndicator label="Chat is working" size={16} />
          ) : (
            <MessageSquareText aria-hidden="true" className="text-foreground-secondary size-4 shrink-0" />
          )}
          <span className="min-w-0 flex-1 truncate">{resume.label}</span>
          {isStreaming && (
            <span className="text-foreground-secondary type-ui-xs flex-shrink-0">
              <ElapsedTime startTime={getTurnStartTime(chatId)} />
            </span>
          )}
        </button>
      </motion.div>
    </MotionConfig>
  )
}
